/**
 * TAP Build Agent WebSocket Service
 *
 * Provides real-time WebSocket connections for TAP Build Agent
 * to enable multi-window synchronization and persistent streaming notifications.
 */

import { getApiBaseUrl as getHttpApiBaseUrl } from '../utils/axios';

const getWsBaseUrl = () => {
  const httpApiBase = getHttpApiBaseUrl();
  const httpOrigin = httpApiBase.replace(/\/?api$/, '');
  const wsOrigin = httpOrigin.replace(/^http/, 'ws');
  return wsOrigin;
};

export type TapBuildWebSocketEventType =
  | 'tap_build_user_message_added'
  | 'tap_build_streaming_started'
  | 'tap_build_streaming_completed'
  | 'tap_build_streaming_cancelled'
  | 'tap_build_streaming_error'
  | 'tap_build_streaming_status'
  | 'connection_established'
  | 'pong'
  | 'error';

export interface TapBuildWebSocketMessage {
  type: TapBuildWebSocketEventType;
  data: {
    message_id?: string;
    project_id?: string;
    content?: string;
    streaming_state_id?: string;
    assistant_message_id?: string;
    status?: string;
    is_streaming?: boolean;
    timestamp?: string;
    message?: string;
    error?: string;
    user_info?: {
      id: string;
      email?: string;
      username?: string;
      firstName?: string;
      lastName?: string;
    };
  };
  timestamp: string;
}

export interface TapBuildWebSocketCallbacks {
  onMessage?: (message: TapBuildWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
  onReconnectAttempt?: (attempt: number) => void;
}

export class TapBuildWebSocket {
  private ws: WebSocket | null = null;
  private projectId: string;
  private token: string;
  private callbacks: TapBuildWebSocketCallbacks;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private isConnecting = false;
  private shouldReconnect = true;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private consecutivePingFailures = 0;
  private maxPingFailures = 3;
  private hasConnectedBefore = false;

  constructor(projectId: string, token: string, callbacks: TapBuildWebSocketCallbacks = {}) {
    this.projectId = projectId;
    this.token = token;
    this.callbacks = callbacks;
  }

  updateCallbacks(callbacks: TapBuildWebSocketCallbacks) {
    this.callbacks = callbacks;
  }

  updateToken(token: string): void {
    this.token = token;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const baseUrl = getWsBaseUrl();
      const wsUrl = `${baseUrl}/api/tap-build-agent/ws/${this.projectId}?token=${this.token}`;

      console.log('🔌 [TAP Build] Attempting WebSocket connection:', {
        baseUrl,
        projectId: this.projectId,
      });

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 [TAP Build] WebSocket connected for project:', this.projectId);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.startPingPong();

        if (this.hasConnectedBefore) {
          console.log('🔄 [TAP Build] WebSocket reconnected for project:', this.projectId);
          this.callbacks.onReconnect?.();
        } else {
          console.log('✅ [TAP Build] WebSocket initial connection for project:', this.projectId);
          this.hasConnectedBefore = true;
          this.callbacks.onConnect?.();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: TapBuildWebSocketMessage = JSON.parse(event.data);
          console.log('📨 [TAP Build] WebSocket message:', message.type, message);

          if (message.type === 'pong') {
            this.consecutivePingFailures = 0;
            console.log('🏓 [TAP Build] Pong received');
            if (this.pongTimeout) {
              clearTimeout(this.pongTimeout);
              this.pongTimeout = null;
            }
            return;
          }

          if (message.type === 'connection_established') {
            console.log('✅ [TAP Build] Connection established for project:', this.projectId);
            return;
          }

          this.callbacks.onMessage?.(message);
        } catch (error) {
          console.error('[TAP Build] Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 [TAP Build] WebSocket disconnected:', {
          projectId: this.projectId,
          code: event.code,
          reason: event.reason,
        });

        this.isConnecting = false;
        this.stopPingPong();
        this.consecutivePingFailures = 0;
        this.callbacks.onDisconnect?.();

        // Don't reconnect for authentication errors (4001-4003) or normal closure (1000)
        const isAuthError = event.code >= 4001 && event.code <= 4003;
        const isNormalClosure = event.code === 1000;
        
        if (!isAuthError && !isNormalClosure && this.shouldReconnect) {
          this.scheduleReconnect();
        } else if (isAuthError) {
          console.error('❌ [TAP Build] Authentication error, stopping reconnection attempts:', event.reason);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ [TAP Build] WebSocket error:', error);
        this.isConnecting = false;
        this.callbacks.onError?.(error);

        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[TAP Build] Error creating WebSocket:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error as Event);

      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 6)),
      this.maxReconnectDelay
    );

    console.log(`🔄 [TAP Build] Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    this.callbacks.onReconnectAttempt?.(this.reconnectAttempts);

    setTimeout(() => {
      if (this.shouldReconnect) {
        console.log(`🔄 [TAP Build] Attempting reconnection ${this.reconnectAttempts}`);
        // Refresh token from localStorage before reconnecting
        const freshToken = localStorage.getItem('token') || '';
        if (freshToken && freshToken !== this.token) {
          console.log('🔑 [TAP Build] Refreshing token for reconnection');
          this.token = freshToken;
        }
        this.connect();
      }
    }, delay);
  }

  private startPingPong(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendPing();
      }
    }, 30000);
  }

  private stopPingPong(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const pingMessage = {
          type: 'ping',
          timestamp: new Date().toISOString(),
        };
        this.ws.send(JSON.stringify(pingMessage));
        console.log('🏓 [TAP Build] Ping sent');

        this.pongTimeout = setTimeout(() => {
          this.consecutivePingFailures++;
          console.warn(`⚠️ [TAP Build] No pong (${this.consecutivePingFailures}/${this.maxPingFailures})`);

          if (this.consecutivePingFailures >= this.maxPingFailures) {
            console.error(`❌ [TAP Build] Too many ping failures, closing connection`);
            this.ws?.close(1000, 'Ping/pong failure');
          }
        }, 10000);
      } catch (error) {
        console.error('❌ [TAP Build] Error sending ping:', error);
        this.consecutivePingFailures++;
      }
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPingPong();

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isOpenOrConnecting(): boolean {
    if (!this.ws) return false;
    return this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING;
  }

  getConnectionState(): 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN' {
    if (!this.ws) return 'CLOSED';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

export class TapBuildWebSocketManager {
  private connections: Map<string, TapBuildWebSocket> = new Map();

  connect(projectId: string, token: string, callbacks: TapBuildWebSocketCallbacks = {}): TapBuildWebSocket {
    const existing = this.connections.get(projectId);
    if (existing && existing.isOpenOrConnecting()) {
      existing.updateCallbacks(callbacks);
      return existing;
    }

    if (existing) {
      existing.disconnect();
      this.connections.delete(projectId);
    }

    const ws = new TapBuildWebSocket(projectId, token, callbacks);
    this.connections.set(projectId, ws);

    ws.connect().catch((error) => {
      console.error('[TAP Build] Failed to connect WebSocket:', projectId, error);
    });

    return ws;
  }

  disconnect(projectId: string): void {
    const ws = this.connections.get(projectId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(projectId);
    }
  }

  getConnection(projectId: string): TapBuildWebSocket | undefined {
    return this.connections.get(projectId);
  }

  disconnectAll(): void {
    this.connections.forEach((ws) => {
      ws.disconnect();
    });
    this.connections.clear();
  }
}

export const tapBuildWebsocketManager = new TapBuildWebSocketManager();
